import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { CheckCircle, XCircle, Clock, Users, BarChart3, AlertCircle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useAuthenticatedUser } from '@/hooks/useAuthenticatedUser';

interface PendingStat {
  stat_id: number;
  player_id: string;
  stat_type: string;
  stat_value: number;
  game_id?: string;
  team_id?: number;
  sport_id?: number;
  created_at: string;
  player_name?: string;
  team_name?: string;
  game_title?: string;
  existing_approvals: any[];
}

interface StatApprovalInterfaceProps {
  userRole: 'player' | 'coach' | 'team-owner' | 'venue';
}

export const StatApprovalInterface: React.FC<StatApprovalInterfaceProps> = ({ userRole }) => {
  const [pendingStats, setPendingStats] = useState<PendingStat[]>([]);
  const [loading, setLoading] = useState(true);
  const [processingStats, setProcessingStats] = useState<Set<number>>(new Set());
  const { user } = useAuthenticatedUser();

  useEffect(() => {
    if (user?.id) {
      loadPendingStats();
    }
  }, [user, userRole]);

  const loadPendingStats = async () => {
    if (!user?.id) return;

    try {
      // For now, show empty stats since tables may not exist
      setPendingStats([]);
    } catch (error) {
      console.error('Error loading pending stats:', error);
      toast.error('Failed to load pending stats');
    } finally {
      setLoading(false);
    }
  };

  const approveStat = async (statId: number) => {
    if (!user?.id) return;

    setProcessingStats(prev => new Set([...prev, statId]));

    try {
      const { error } = await supabase.rpc('process_stat_approval', {
        p_stat_id: statId,
        p_approver_id: user.id,
        p_approver_role: userRole
      });

      if (error) throw error;

      toast.success('Stat approved successfully');
      loadPendingStats();
    } catch (error) {
      console.error('Error approving stat:', error);
      toast.error('Failed to approve stat');
    } finally {
      setProcessingStats(prev => {
        const next = new Set(prev);
        next.delete(statId);
        return next;
      });
    }
  };

  const rejectStat = async (statId: number) => {
    if (!user?.id) return;

    setProcessingStats(prev => new Set([...prev, statId]));

    try {
      // For now, just remove the approval if it exists
      const { error } = await supabase
        .from('stat_approvals')
        .delete()
        .eq('stat_id', statId)
        .eq('approver_id', user.id);

      if (error) throw error;

      toast.success('Stat approval removed');
      loadPendingStats();
    } catch (error) {
      console.error('Error rejecting stat:', error);
      toast.error('Failed to reject stat');
    } finally {
      setProcessingStats(prev => {
        const next = new Set(prev);
        next.delete(statId);
        return next;
      });
    }
  };

  const getStatTypeDisplay = (type: string) => {
    return type.charAt(0).toUpperCase() + type.slice(1).replace('_', ' ');
  };

  const hasUserApproved = (stat: PendingStat) => {
    return stat.existing_approvals.some(approval => approval.approver_id === user?.id);
  };

  const getApprovalStatus = (stat: PendingStat) => {
    const playerApproved = stat.existing_approvals.some(a => a.approver_role === 'player');
    const authorityApproved = stat.existing_approvals.some(a => 
      ['coach', 'team-owner', 'venue'].includes(a.approver_role)
    );

    if (playerApproved && authorityApproved) {
      return { status: 'ready', text: 'Ready for confirmation', color: 'success' };
    } else if (playerApproved) {
      return { status: 'partial', text: 'Player approved, needs authority', color: 'warning' };
    } else if (authorityApproved) {
      return { status: 'partial', text: 'Authority approved, needs player', color: 'warning' };
    }
    return { status: 'pending', text: 'Pending approvals', color: 'secondary' };
  };

  if (loading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <Card key={i} className="animate-pulse">
            <CardContent className="p-6">
              <div className="h-4 bg-muted rounded w-3/4 mb-2"></div>
              <div className="h-3 bg-muted rounded w-1/2"></div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (pendingStats.length === 0) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <BarChart3 className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-semibold mb-2">No Pending Stats</h3>
          <p className="text-muted-foreground">
            {userRole === 'player' 
              ? "You don't have any stats waiting for approval"
              : "No stats require your approval at this time"
            }
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">
          {userRole === 'player' ? 'Your Pending Stats' : 'Stats Requiring Approval'}
        </h3>
        <Badge variant="outline">
          {pendingStats.length} pending
        </Badge>
      </div>

      {pendingStats.map((stat) => {
        const approvalStatus = getApprovalStatus(stat);
        const userApproved = hasUserApproved(stat);
        const isProcessing = processingStats.has(stat.stat_id);

        return (
          <Card key={stat.stat_id}>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base">
                    {getStatTypeDisplay(stat.stat_type)}: {stat.stat_value}
                  </CardTitle>
                  <p className="text-sm text-muted-foreground">
                    Player: {stat.player_name}
                  </p>
                </div>
                <Badge variant={approvalStatus.color as any}>
                  {approvalStatus.text}
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="text-sm text-muted-foreground">
                  <p>Submitted: {new Date(stat.created_at).toLocaleDateString()}</p>
                  {stat.existing_approvals.length > 0 && (
                    <p>
                      Approvals: {stat.existing_approvals.map(a => a.approver_role).join(', ')}
                    </p>
                  )}
                </div>

                {userRole !== 'player' && !userApproved && (
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={() => approveStat(stat.stat_id)}
                      disabled={isProcessing}
                      className="flex-1"
                    >
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Approve
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => rejectStat(stat.stat_id)}
                      disabled={isProcessing}
                      className="flex-1"
                    >
                      <XCircle className="h-4 w-4 mr-2" />
                      Reject
                    </Button>
                  </div>
                )}

                {userApproved && (
                  <div className="flex items-center gap-2 text-sm text-green-600">
                    <CheckCircle className="h-4 w-4" />
                    You have approved this stat
                  </div>
                )}

                {userRole === 'player' && (
                  <div className="text-sm text-muted-foreground">
                    <AlertCircle className="h-4 w-4 inline mr-2" />
                    This stat requires approval from team management or venue staff
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};